import { Injectable } from "@angular/core";
import {
  catchError,
  EMPTY,
  exhaustMap,
  from,
  Observable,
  takeWhile,
  throwError,
  timer,
} from "rxjs";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { CryptoService } from "@bitwarden/common/abstractions/crypto.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { Utils } from "@bitwarden/common/misc/utils";
import { ApproveDeviceRequest } from "@bitwarden/common/models/request/approve-device.request";
import { CreateDeviceApprovalRequest } from "@bitwarden/common/models/request/create-device-approval.request";
import { DeviceApprovalRequestResponse } from "@bitwarden/common/models/response/device-approval-request.response";

import { DeviceTrustService } from "./device-trust.service";
import { ReturningDeviceUnlockService } from "./returning-device-unlock.service";

export const DEVICE_APPROVAL_POLL_INTERVAL_MS = 4000;

/**
 * TDE new-device approval (GOOGLE_SSO_PLAN.md, Phase 3). Covers both sides:
 * - Requester (an unrecognized device, `DeviceApprovalRequired: true` on login): creates a
 *   request carrying this device's public key, polls until it's resolved, then unlocks with the
 *   UserKey an already-trusted device wrapped for it.
 * - Approver (an already-trusted, unlocked device): lists pending requests and wraps its
 *   in-memory UserKey under the requesting device's public key.
 *
 * The server only relays the requesting device's public key, so a compromised server could
 * swap in its own. `getFingerprintPhrase` is shown on both screens and the approver must
 * confirm they match before approving — see `PendingDeviceApprovalDialogComponent`.
 */
@Injectable({
  providedIn: "root",
})
export class DeviceApprovalService {
  constructor(
    private deviceTrustService: DeviceTrustService,
    private returningDeviceUnlockService: ReturningDeviceUnlockService,
    private cryptoService: CryptoService,
    private stateService: StateService,
    private apiService: ApiService,
    private logService: LogService,
  ) {}

  /**
   * Requester: asks for approval with this device's public key. The backend treats a repeat
   * call from the same device as idempotent (returns the existing pending request), so a page
   * refresh on the waiting screen can safely call this again.
   *
   * Rejects a response carrying any other public key: the fingerprint phrase only catches a
   * swapped key if it's built from this device's own key, not one the server echoes back.
   */
  async createRequest(deviceName?: string): Promise<DeviceApprovalRequestResponse> {
    const { publicKeySpki } = await this.deviceTrustService.getOrCreateDeviceKeyPair();
    const publicKey = Utils.fromBufferToB64(publicKeySpki);
    const request = await this.apiService.postDeviceApprovalRequest(
      new CreateDeviceApprovalRequest(publicKey, deviceName),
    );
    if (request.publicKey !== publicKey) {
      throw new Error("Server returned a different device public key");
    }
    return request;
  }

  /**
   * Requester: polls the request until it leaves `pending`. Emits every response, including the
   * final one; a failed poll is logged and skipped so a transient network error doesn't end
   * the wait. Once the request's `expiresAt` has passed, a failed poll errors the stream
   * instead: it can no longer be approved, so waiting on a server that won't answer is pointless.
   */
  watchRequest(
    request: DeviceApprovalRequestResponse,
    intervalMs = DEVICE_APPROVAL_POLL_INTERVAL_MS,
  ): Observable<DeviceApprovalRequestResponse> {
    const deadline = Date.parse(request.expiresAt);
    return timer(0, intervalMs).pipe(
      exhaustMap(() =>
        from(this.apiService.getDeviceApprovalRequest(request.id)).pipe(
          catchError((e: unknown) => {
            // String() rather than `e.message`: capSend rejects with ErrorResponse, which isn't
            // an Error but has a toString() that returns the server's message.
            this.logService.error(String(e));
            return Date.now() >= deadline ? throwError(() => e) : EMPTY;
          }),
        ),
      ),
      takeWhile((response) => response.status === "pending", true),
    );
  }

  /** Requester: unlocks with the UserKey an approving device wrapped for this device. */
  async unlockWithApprovedRequest(response: DeviceApprovalRequestResponse): Promise<void> {
    if (response.status !== "approved" || response.encryptedUserKey == null) {
      throw new Error("Device approval request is not approved");
    }
    await this.returningDeviceUnlockService.unlockWithLocalDeviceKey(
      response.encryptedUserKey,
      response.privateKey,
    );
  }

  /**
   * Both sides: human-comparable phrase derived from the account email and the requesting
   * device's public key (same fingerprint scheme as Bitwarden's login-with-device).
   */
  async getFingerprintPhrase(publicKeyB64: string): Promise<string> {
    const email = await this.stateService.getEmail();
    const fingerprint = await this.cryptoService.getFingerprint(
      email,
      Utils.fromB64ToArray(publicKeyB64).buffer,
    );
    return fingerprint.join("-");
  }

  /**
   * Approver: whether this device can approve others — i.e. it has itself unlocked the current
   * account with its own device key. False for password-based accounts, which never have
   * approval requests, and for an account that only some other user unlocked on this browser.
   */
  async isTrustedDevice(): Promise<boolean> {
    return this.deviceTrustService.isTrustedFor(await this.stateService.getUserId());
  }

  async listPending(): Promise<DeviceApprovalRequestResponse[]> {
    return (await this.apiService.getPendingDeviceApprovalRequests()).data;
  }

  /**
   * Approver: wraps this device's in-memory UserKey under the requesting device's public key.
   * Must only be called after the user confirmed the fingerprint phrase for this same
   * `request` — the key wrapped for is `request.publicKey`, nothing re-fetched.
   */
  async approve(request: DeviceApprovalRequestResponse): Promise<void> {
    const userKey = await this.cryptoService.getKey();
    if (userKey == null) {
      throw new Error("Vault must be unlocked to approve a device");
    }

    const requesterPublicKey = await this.deviceTrustService.importPublicKey(
      Utils.fromB64ToArray(request.publicKey).buffer,
    );
    const wrapped = await this.deviceTrustService.wrapUserKey(userKey, requesterPublicKey);
    await this.apiService.postApproveDeviceRequest(
      request.id,
      new ApproveDeviceRequest(wrapped.encryptedString),
    );
  }

  async deny(id: string): Promise<void> {
    await this.apiService.postDenyDeviceRequest(id);
  }
}
